/**
 * ErrorBoundary — top-level guard so a thrown render in any deeply-nested
 * component doesn't blank the whole admin dashboard or client portal
 * (PR #31, GO_LIVE_PUNCHLIST R1).
 *
 * Catches synchronous render errors and JS exceptions in the React tree
 * below it. Async errors (network calls, etc) are NOT caught here — those
 * still need their own try/catch + toast in the call-site, which we already
 * do throughout the app.
 *
 * UX: shows a polite "something went wrong" panel with a Reload button
 * and (in dev) the error message + stack. Keeps the app navigable instead
 * of dropping the user on a blank white page.
 *
 * W1-01 (2026-08-01) — reports to Sentry. `index.js` initialises the SDK via
 * a dynamic `import("@sentry/react")` and never puts it on `window`, so
 * `window.Sentry` was always undefined here — the old commented-out hook
 * could never have worked even uncommented. We do the same dynamic import;
 * ES module imports are cached, so this resolves to the exact client
 * `Sentry.init()` already configured in index.js, and is a safe no-op if
 * no DSN was set (no client bound, nothing sent) or the SDK failed to load.
 * Context is limited to the route (pathname only, no query/hash — those can
 * carry tokens) and the user's role decoded from the JWT payload already
 * sitting in localStorage. Never the user's email or any client/deal data.
 */
import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

// Best-effort role decode from the JWT already stored for auth — no network
// call, no dependency on AuthContext (which mounts *inside* this boundary,
// so its context isn't reachable from here). Malformed/missing token just
// yields undefined; this must never throw.
function currentUserRole() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const payload = token.split(".")[1];
    if (!payload) return undefined;
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return decoded?.role || undefined;
  } catch {
    return undefined;
  }
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught error:", error, errorInfo);
    this.setState({ errorInfo });

    import("@sentry/react")
      .then((Sentry) => {
        try {
          Sentry.captureException(error, {
            contexts: {
              react: { componentStack: errorInfo?.componentStack },
            },
            tags: {
              route: (typeof window !== "undefined" && window.location?.pathname) || "unknown",
              user_role: currentUserRole() || "unknown",
            },
          });
        } catch (captureErr) {
          // eslint-disable-next-line no-console
          console.warn("[ErrorBoundary] Sentry capture failed", captureErr);
        }
      })
      .catch(() => {
        /* Sentry SDK not available — console.error above is the fallback record */
      });
  }

  handleReload = () => {
    // Soft-reset state in case the error was transient and a re-render fixes
    // it. If not, the user clicks the Reload button which does a full
    // browser reload.
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleHardReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";
    const errorMessage = this.state.error?.message || String(this.state.error || "Unknown error");

    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center px-4 py-10">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm max-w-lg w-full p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h1 className="text-lg font-semibold text-[#021d49]">Something went wrong</h1>
              <p className="text-sm text-gray-600 mt-1">
                The page hit an unexpected error. Reloading usually fixes it. If it keeps happening,
                send us a note at <a href="mailto:loans@sgcap.com.au" className="text-[#bd9a59] hover:underline">loans@sgcap.com.au</a>.
              </p>
            </div>
          </div>

          {isDev && (
            <details className="mt-4 mb-4 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-3">
              <summary className="cursor-pointer font-medium text-gray-700">Developer details</summary>
              <pre className="whitespace-pre-wrap break-all mt-2 text-[11px]">{errorMessage}</pre>
              {this.state.errorInfo?.componentStack && (
                <pre className="whitespace-pre-wrap break-all mt-2 text-[11px] text-gray-500">
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mt-6">
            <button
              onClick={this.handleReload}
              className="flex-1 px-4 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              data-testid="error-boundary-soft-reload"
            >
              Try again
            </button>
            <button
              onClick={this.handleHardReload}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-[#021d49] text-white rounded hover:bg-[#052b66]"
              data-testid="error-boundary-hard-reload"
            >
              <RefreshCw className="w-4 h-4" /> Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
